// Figma: 5287:18364 — see stories/blocks/stat-counter.stories.tsx
"use client";

// Odometer-style count-up reveal for headline stats like "300+" on the careers
// hero. Animates only the trailing digits (default: last two) so smaller values
// still feel polished without rolling the whole number.

import NumberFlow from "@number-flow/react";
import * as React from "react";

import { useIntersectedOnce } from "@/hooks/use-intersection-once";
import { formatLocalizedCurrency } from "@/lib/localize-currency";
import { cn } from "@/lib/utils";

interface StatCounterProps {
  value: number;
  locale?: string;
  suffix?: string;
  className?: string;
  /** How many trailing digits should roll. Defaults to 2 (e.g. 3 + 00 → 300). */
  animatedDigits?: number;
  /** Total animation duration in ms. Defaults to 700. */
  durationMs?: number;
  /** When set, animates the full number from this value to `value` (ignores animatedDigits split). */
  startValue?: number;
  /** Intermediate values to pause at during range-mode count-up (requires `startValue`). */
  stops?: number[];
  /** Hold duration at each stop in ms. Defaults to 450. */
  stopPauseMs?: number;
  /** ISO currency code for range-mode formatting. */
  currency?: string;
}

function splitValue(value: number, animatedDigits: number) {
  const divisor = 10 ** animatedDigits;
  return {
    prefix: Math.floor(value / divisor),
    suffix: value % divisor,
  };
}

function formatSuffix(value: number, animatedDigits: number) {
  return String(value).padStart(animatedDigits, "0");
}

function formatCurrency(value: number, locale: string, currency: string) {
  return formatLocalizedCurrency(value, locale, {
    currency,
    maximumFractionDigits: 0,
  });
}

export function StatCounter({
  value,
  locale = "en",
  suffix,
  className,
  animatedDigits = 2,
  durationMs = 700,
  startValue,
  stops,
  stopPauseMs = 450,
  currency,
}: StatCounterProps) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const isVisible = useIntersectedOnce(ref, {
    root: null,
    rootMargin: "0px",
    threshold: 0.25,
  });

  const isRangeMode = startValue !== undefined;

  const { prefix, suffix: targetSuffix } = splitValue(value, animatedDigits);
  const [mounted, setMounted] = React.useState(false);
  const [currentSuffix, setCurrentSuffix] = React.useState(targetSuffix);
  const [currentValue, setCurrentValue] = React.useState(startValue ?? value);
  const [hasAnimated, setHasAnimated] = React.useState(false);

  // Defer NumberFlow until after hydration so SSR text matches the first
  // client paint, then run the scramble when the counter enters the viewport.
  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!mounted || !isVisible || hasAnimated) return;

    if (isRangeMode) {
      const from = startValue;
      let cancelled = false;
      const timeouts: number[] = [];
      const intervals: number[] = [];

      const wait = (ms: number) =>
        new Promise<void>((resolve) => {
          const id = window.setTimeout(() => {
            if (!cancelled) resolve();
          }, ms);
          timeouts.push(id);
        });

      const animateSegment = (
        segmentFrom: number,
        segmentTo: number,
        ms: number
      ) =>
        new Promise<void>((resolve) => {
          const STEPS = 24;
          let step = 0;

          const id = window.setInterval(() => {
            if (cancelled) {
              window.clearInterval(id);
              resolve();
              return;
            }

            step += 1;
            if (step >= STEPS) {
              setCurrentValue(segmentTo);
              window.clearInterval(id);
              resolve();
              return;
            }

            const progress = step / STEPS;
            setCurrentValue(
              Math.round(segmentFrom + (segmentTo - segmentFrom) * progress)
            );
          }, ms / STEPS);

          intervals.push(id);
        });

      void (async () => {
        const waypoints =
          stops && stops.length > 0 ? [from, ...stops, value] : [from, value];
        const segmentCount = waypoints.length - 1;
        const pauseTotal = Math.max(0, segmentCount - 1) * stopPauseMs;
        const animateMs = Math.max(durationMs - pauseTotal, segmentCount * 250);
        const segmentDuration = animateMs / segmentCount;

        for (let index = 0; index < segmentCount; index += 1) {
          if (cancelled) return;

          await animateSegment(
            waypoints[index]!,
            waypoints[index + 1]!,
            segmentDuration
          );

          if (index < segmentCount - 1) {
            await wait(stopPauseMs);
          }
        }

        if (cancelled) return;
        setCurrentValue(value);
        setHasAnimated(true);
      })();

      return () => {
        cancelled = true;
        for (const id of intervals) window.clearInterval(id);
        for (const id of timeouts) window.clearTimeout(id);
      };
    }

    const STEPS = 12;
    let step = 0;
    const maxSuffix = 10 ** animatedDigits - 1;

    const id = window.setInterval(() => {
      step += 1;
      if (step >= STEPS) {
        setCurrentSuffix(targetSuffix);
        setHasAnimated(true);
        window.clearInterval(id);
        return;
      }
      const progress = step / STEPS;
      // Full-range scramble on the suffix band (important when target ends in 00).
      const noise = maxSuffix * (1 - progress) * Math.random();
      setCurrentSuffix(
        Math.min(
          maxSuffix,
          Math.max(0, Math.round(targetSuffix * progress + noise))
        )
      );
    }, durationMs / STEPS);

    return () => window.clearInterval(id);
  }, [
    animatedDigits,
    durationMs,
    hasAnimated,
    isRangeMode,
    isVisible,
    mounted,
    startValue,
    stopPauseMs,
    stops,
    targetSuffix,
    value,
  ]);

  const staticLabel = isRangeMode
    ? currency
      ? formatCurrency(startValue ?? value, locale, currency)
      : String(startValue ?? value)
    : `${prefix > 0 ? prefix : ""}${formatSuffix(targetSuffix, animatedDigits)}${suffix ?? ""}`;

  if (!mounted) {
    return (
      <span
        ref={ref}
        className={cn("inline-flex items-baseline tabular-nums", className)}
      >
        {staticLabel}
      </span>
    );
  }

  if (isRangeMode) {
    return (
      <span
        ref={ref}
        className={cn("inline-flex items-baseline tabular-nums", className)}
      >
        <NumberFlow
          value={hasAnimated ? value : currentValue}
          locales={locale}
          className={className}
          format={
            currency
              ? {
                  style: "currency",
                  currency,
                  maximumFractionDigits: 0,
                }
              : { maximumFractionDigits: 0 }
          }
        />
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className={cn("inline-flex items-baseline tabular-nums", className)}
    >
      {prefix > 0 ? <span>{prefix}</span> : null}
      <NumberFlow
        value={hasAnimated ? targetSuffix : currentSuffix}
        locales={locale}
        format={{
          minimumIntegerDigits: animatedDigits,
          maximumFractionDigits: 0,
        }}
      />
      {suffix}
    </span>
  );
}
